// src/hr/timeoff/MyTeamPage.js -- "My team" (spec 16.5 C), for managers: their direct reports (name,
// title, department) and who of them is out, this week and by month. Names only -- never a type,
// hours, a note or a balance. The server limits both lists to the manager's direct reports
// (GET /api/hr/me/team and GET /api/hr/whos-out?scope=team) and answers 403 to anyone else.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiUsers } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Card, EmptyState, Skeleton } from '../kit';
import WhosOutCalendar from '../WhosOutCalendar';

const NO_TEAM = "You don't manage anyone yet";

// GET /me/team. status: 'loading' | 'ready' | 'none' (403: no direct reports) | 'error'.
function useMyTeam() {
  const [state, setState] = useState({ status: 'loading', team: [], message: '' });
  const [attempt, setAttempt] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    latest.current += 1;
    const call = latest.current;
    let live = true;
    setState((s) => ({ ...s, status: 'loading' }));
    Promise.resolve()
      .then(() => hr.myTeam())
      .then((body) => {
        if (live && call === latest.current) setState({ status: 'ready', team: (body && body.team) || [], message: '' });
      })
      .catch((err) => {
        if (!live || call !== latest.current) return;
        const none = !!(err && err.response && err.response.status === 403);
        setState({ status: none ? 'none' : 'error', team: [], message: none ? errorText(err, NO_TEAM) : '' });
      });
    return () => { live = false; };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

const nameOf = (p) => [p.firstName, p.lastName].filter(Boolean).join(' ');

function TeamCard({ p, delay }) {
  const name = nameOf(p);
  return (
    <li className="hrp-team__it">
      <Card delay={delay} className="hrp-team__card">
        <Avatar id={p.id} name={name} size="lg" />
        <div className="hrp-team__txt">
          <b className="hrp-team__name">{name}</b>
          <span className="hrp-team__title">{p.title || <span className="hrp-dim">No title yet</span>}</span>
          {p.department && <span className="hrp-team__dept">{p.department}</span>}
        </div>
      </Card>
    </li>
  );
}

function LoadingTeam() {
  return (
    <div className="hrp-team__grid" aria-busy="true">
      <Skeleton label="Loading your team" variant="block" height={88} />
      <Skeleton variant="block" height={88} />
      <Skeleton variant="block" height={88} />
    </div>
  );
}

export function MyTeamPage({ today }) {
  const { status, team, message, retry } = useMyTeam();

  let sub = 'The people who report to you';
  if (status === 'ready') sub = `${team.length} ${team.length === 1 ? 'person reports' : 'people report'} to you`;

  let body;
  if (status === 'none') {
    body = (
      <Card>
        <EmptyState icon={FiUsers} title={message || NO_TEAM}>
          When HR sets you as someone's manager, they show up here with who's out.
        </EmptyState>
      </Card>
    );
  } else if (status === 'error') {
    body = (
      <Card>
        <EmptyState compact icon={FiAlertCircle} title="Couldn't load your team" action={<Btn variant="ghost" size="sm" onClick={retry}>Try again</Btn>}>
          Something went wrong on our side. Try again in a moment.
        </EmptyState>
      </Card>
    );
  } else {
    body = (
      <>
        {status === 'loading' && team.length === 0 ? <LoadingTeam /> : (
          <ul className="hrp-team__grid" aria-label="Your direct reports">
            {team.map((p, i) => <TeamCard key={p.id} p={p} delay={Math.min(i, 5)} />)}
          </ul>
        )}
        <section className="hrp-team__out" aria-label="Who's out on your team">
          <Card delay={2} title="Out this week" className="hrp-wocard hrp-team__wk">
            <WhosOutCalendar mode="week" today={today} scope="team" />
          </Card>
          <Card delay={3} className="hrp-calcard hrp-team__cal">
            <WhosOutCalendar mode="month" today={today} scope="team" />
          </Card>
          <p className="hrp-team__note">Approved time away, Monday to Friday. Names only — never the reason.</p>
        </section>
      </>
    );
  }

  return (
    <div className="hrp-page hrp-team">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">My team</h1>
          <p className="hrp-sub">{sub}</p>
        </div>
      </div>
      {body}
    </div>
  );
}

export default MyTeamPage;
